import { CKButton } from '@clearkit/react';
import get from 'lodash/get';
import { Mutation, Query } from 'react-apollo';
import { Link } from 'react-router-dom';

import Footer from '~/components/Footer';
import GraphqlError from '~/components/GraphqlError';
import LoadingArea from '~/components/LoadingArea';
import ToastsContainer from '~/containers/Toasts';
import Check from '~/images/destinations/Check';
import {
  destinationNameForType,
  destinationSubtitleForType,
  destinationTypeCustomHtml,
  destinationTypeEmail,
  destinationTypeSalesforce,
  destinationTypeSlack,
  destinationTypeWebhook,
} from '~/lib/destinations';
import history from '~/lib/history';
import paths from '~/lib/paths';
import { trackDestinationCreated } from '~/lib/segmentAnalytics.js';
import { legacySegmentType } from '~/lib/segments';
import Logo from '~/pages/destinations/components/Logo';
import Notice from '~/pages/destinations/components/Notice';

import sharedStyles from '../Edit/styles.js';
import Arrow from './arrow.svg';
import CustomHtml from './CustomHtml';
import Email from './Email';
import mutation from './mutation.graphql';
import query from './query.graphql';
import Salesforce from './Salesforce';
import Slack from './Slack';
import styles from './styles';
import tile from './tile.png';
import Webhook from './Webhook';

class SegmentDestinationNewPage extends React.Component {
  state = {
    errors: [],
    input: {},
  };

  handleInputChange = (input) => {
    this.setState({ input });
  };

  handleCreate = (createDestination) => {
    const { segmentId, segmentType, type } = this.props;
    const { input } = this.state;

    createDestination({
      variables: {
        input: {
          segmentId,
          segmentType,
          type,
          ...input,
        },
      },
    });
  };

  handleComplete = ({ response: { node: destination, errors } }) => {
    if (errors.length) {
      this.setState({ errors });
      ToastsContainer.addError('There was an error saving the destination');
      return;
    }

    const { segment } = destination;

    trackDestinationCreated(destination);
    ToastsContainer.addSuccess('Destination saved');

    const path = paths.segmentDestinations(segment.type, segment.id);
    history.push(path);
  };

  render() {
    const { classes, segmentId, segmentType, type } = this.props;
    const { errors } = this.state;

    const attributeType = legacySegmentType(segmentType);

    const variables = {
      attributeType,
      includeSalesforceAccountConnector: type === destinationTypeSalesforce,
      includeSlackAccountConnector: type === destinationTypeSlack,
      segmentId,
      segmentType,
    };

    return (
      <form className={classes.root}>
        <Query query={query} variables={variables}>
          {({ data = {}, error, loading, refetch }) => {
            if (loading) {
              return <LoadingArea loading />;
            }

            const { segment, attributes } = data;

            const destinationName = destinationNameForType(type);
            const destinationSubtitle = destinationSubtitleForType(
              type,
              get(segment, 'type'),
            );

            return (
              <>
                <div className={classes.header}>
                  <h1 className={classes.heading}>
                    Set up {destinationName} Destination
                  </h1>

                  <span className={classes.subtitle}>
                    {destinationSubtitle}
                  </span>

                  <div className={classes.flow}>
                    <div className={classes.flowBox}>
                      <img alt="" className={classes.tile} src={tile} />
                      <div className={classes.flowDetail}>
                        <span>{get(segment, 'name', 'Unknown Segment')}</span>
                        <span className={classes.detailSub}>
                          {get(
                            segment,
                            'collection.name',
                            'Unknown Collection',
                          )}
                        </span>
                      </div>
                    </div>

                    <Arrow className={classes.flowArrow} />

                    <div className={classes.flowBox}>
                      <div className={classes.logo}>
                        <Logo destinationType={type} width="40" height="40" />
                      </div>

                      <div className={classes.flowDetail}>
                        <span>{destinationName}</span>
                        <span className={classes.detailSub}>
                          <Check className={classes.check} />
                          Configuring Destination
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {error ? (
                  <GraphqlError
                    error={error}
                    refetch={refetch}
                    loading={loading}
                  />
                ) : (
                  <>
                    <Notice type={type} typename={segment.type} />

                    <div className={classes.content}>
                      {(() => {
                        switch (type) {
                          case destinationTypeCustomHtml:
                            return (
                              <CustomHtml
                                errors={errors}
                                onInputChange={this.handleInputChange}
                                typename={segment.type}
                              />
                            );

                          case destinationTypeEmail:
                            return (
                              <Email
                                attributes={attributes}
                                errors={errors}
                                onInputChange={this.handleInputChange}
                                segmentId={segmentId}
                                segmentType={segmentType}
                                typename={segment.type}
                              />
                            );

                          case destinationTypeSalesforce:
                            return (
                              <Salesforce
                                accountConnector={
                                  data.salesforceAccountConnector
                                }
                                errors={errors}
                                onInputChange={this.handleInputChange}
                                refetch={refetch}
                                typename={segment.type}
                              />
                            );

                          case destinationTypeSlack:
                            return (
                              <Slack
                                accountConnector={data.slackAccountConnector}
                                attributes={attributes}
                                errors={errors}
                                onInputChange={this.handleInputChange}
                                segmentId={segmentId}
                                segmentType={segmentType}
                                refetch={refetch}
                                typename={segment.type}
                              />
                            );

                          case destinationTypeWebhook:
                            return (
                              <Webhook
                                attributes={attributes}
                                errors={errors}
                                onInputChange={this.handleInputChange}
                                segmentId={segmentId}
                                segmentType={segmentType}
                                typename={segment.type}
                              />
                            );

                          default:
                            return null;
                        }
                      })()}
                    </div>

                    <Footer
                      right={
                        <>
                          <CKButton
                            as={Link}
                            to={paths.segmentDestinations(
                              segment.type,
                              segment.id,
                            )}
                            variant="secondary">
                            Cancel
                          </CKButton>
                          <Mutation
                            mutation={mutation}
                            onCompleted={this.handleComplete}
                            refetchQueries={['SegmentDestinationsPage']}>
                            {(createDestination, { loading }) => (
                              <CKButton
                                disabled={loading}
                                onClick={() => {
                                  this.handleCreate(createDestination);
                                }}
                                type="button"
                                variantColor="green"
                                variant="bold">
                                Save changes
                              </CKButton>
                            )}
                          </Mutation>
                        </>
                      }
                    />
                  </>
                )}
              </>
            );
          }}
        </Query>
      </form>
    );
  }
}

SegmentDestinationNewPage.propTypes = {
  classes: PropTypes.object.isRequired,
  type: PropTypes.oneOf([
    'custom_html',
    'email',
    'salesforce',
    'slack',
    'webhook',
  ]).isRequired,
};

export default withStyles(
  (theme) => ({
    ...styles(theme),
    ...sharedStyles(theme),
  }),
  { name: 'SegmentDestinationNewPage' },
)(SegmentDestinationNewPage);
